// FUNCTIONALITY-115C-R2 — shared seller contacts + FOP payment block (single SoT readers).
import { useState } from "react";
import { copyText } from "./contact-manager-v2.js";
import { ContactActionButton, ContactActionFeedback, type ContactFeedback } from "./contact-actions-v2.js";
import {
  formatSellerContactsText,
  formatSellerPaymentRequisitesText,
  STORE_INFO,
} from "./store-info-v2.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { t } from "./store-v2-ui-copy.js";

function useCopyFlash(lang: StoreV2Lang) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<ContactFeedback | null>(null);
  const [manualCopy, setManualCopy] = useState<string | null>(null);
  const copiedLabel = lang === "en" ? "Copied ✓" : "Скопійовано ✓";

  const copyField = async (key: string, value: string) => {
    if (await copyText(value)) {
      setCopiedKey(key);
      setManualCopy(null);
      setFeedback({ kind: "ok", text: copiedLabel });
      window.setTimeout(() => {
        setCopiedKey(null);
        setFeedback(null);
      }, 1800);
    } else {
      setCopiedKey(null);
      setManualCopy(value);
      setFeedback(null);
    }
  };

  return { copiedKey, feedback, setFeedback, manualCopy, copyField, copiedLabel };
}

function CopyRow({
  label,
  value,
  copyKey,
  copyValue = value,
  copiedKey,
  onCopy,
  copiedLabel,
  copyLabel,
}: {
  label: string;
  value: string;
  copyKey: string;
  copyValue?: string;
  copiedKey: string | null;
  onCopy: (key: string, value: string) => void;
  copiedLabel: string;
  copyLabel: string;
}) {
  return (
    <div className="sv2-fop-row">
      <div className="sv2-fop-row-text">
        <span className="sv2-fop-label">{label}</span>
        <span className="sv2-fop-value">{value}</span>
      </div>
      <button
        type="button"
        className="sv2-copy-btn sv2-copy-inline"
        onClick={() => void onCopy(copyKey, copyValue)}
      >
        {copiedKey === copyKey ? copiedLabel : copyLabel}
      </button>
    </div>
  );
}

/** Interactive phone / email / address + Google Maps (external-link adapter). */
export function SellerContactsBlock({
  lang,
  testId = "sv2-about-contacts-once",
}: {
  lang: StoreV2Lang;
  testId?: string;
}) {
  const { copiedKey, feedback, setFeedback, manualCopy, copyField, copiedLabel } = useCopyFlash(lang);
  const copyLabel = t(lang, "copy");

  return (
    <div className="sv2-info-card sv2-fop-compact" data-testid={testId} id="sv2-seller-anchor">
      <div className="sv2-meta-label" style={{ padding: "8px 10px 0" }}>
        {lang === "en" ? "Contacts" : "Контакти"}
      </div>

      <div className="sv2-contact-action-row">
        <ContactActionButton
          lang={lang}
          type="phone"
          value={STORE_INFO.phoneTel}
          copyFallback={STORE_INFO.phoneDisplay}
          label={STORE_INFO.phoneDisplay}
          icon="phone"
          className="sv2-about-link sv2-contact-inline"
          onFeedback={setFeedback}
        />
        <button
          type="button"
          className="sv2-copy-btn sv2-copy-inline"
          onClick={() => void copyField("phone", STORE_INFO.phoneTel)}
        >
          {copiedKey === "phone" ? copiedLabel : copyLabel}
        </button>
      </div>

      <div className="sv2-contact-action-row">
        <ContactActionButton
          lang={lang}
          type="email"
          value={STORE_INFO.email}
          copyFallback={STORE_INFO.email}
          label={STORE_INFO.email}
          icon="mail"
          className="sv2-about-link sv2-contact-inline"
          onFeedback={setFeedback}
        />
        <button
          type="button"
          className="sv2-copy-btn sv2-copy-inline"
          onClick={() => void copyField("email", STORE_INFO.email)}
        >
          {copiedKey === "email" ? copiedLabel : copyLabel}
        </button>
      </div>

      <div className="sv2-fop-row">
        <div className="sv2-fop-row-text">
          <span className="sv2-fop-label">{lang === "en" ? "Address" : "Адреса"}</span>
          <span className="sv2-fop-value">{STORE_INFO.address}</span>
        </div>
      </div>
      <ContactActionButton
        lang={lang}
        type="maps"
        value={STORE_INFO.mapsUrl}
        copyFallback={STORE_INFO.address}
        label={lang === "en" ? "Open in Google Maps" : "Відкрити в Google Maps"}
        icon="map"
        className="sv2-copy-btn"
        onFeedback={setFeedback}
      />

      <button
        type="button"
        className="sv2-copy-btn"
        style={{ margin: 10 }}
        onClick={() => void copyField("all", formatSellerContactsText(lang))}
      >
        {copiedKey === "all" ? copiedLabel : lang === "en" ? "Copy contacts" : "Скопіювати контакти"}
      </button>

      <ContactActionFeedback feedback={feedback} />
      {manualCopy ? (
        <div className="sv2-contact-feedback sv2-contact-feedback-info" style={{ margin: 10 }}>
          {t(lang, "copy_manually")}
          <textarea
            className="sv2-manual-copy"
            readOnly
            value={manualCopy}
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
      ) : null}
    </div>
  );
}

/** Reusable FOP / seller payment requisites (About + payment section). */
export function SellerPaymentBlock({
  lang,
  testId = "sv2-about-fop-once",
  showTitle = true,
  compact = false,
  anchorId,
}: {
  lang: StoreV2Lang;
  testId?: string;
  showTitle?: boolean;
  compact?: boolean;
  /** Set only on the primary seller block (avoid duplicate DOM ids). */
  anchorId?: string;
}) {
  const { copiedKey, feedback, manualCopy, copyField, copiedLabel } = useCopyFlash(lang);
  const copyLabel = t(lang, "copy");

  return (
    <div
      className={`sv2-info-card sv2-fop-compact${compact ? " sv2-fop-embedded" : ""}`}
      data-testid={testId}
      {...(anchorId ? { id: anchorId } : {})}
    >
      {showTitle ? (
        <div className="sv2-meta-label" style={{ padding: "8px 10px 0" }}>
          {lang === "en" ? "Seller payment details" : "Реквізити для оплати на рахунок ФОП"}
        </div>
      ) : null}
      <CopyRow
        label={lang === "en" ? "Recipient" : "Отримувач"}
        value={STORE_INFO.merchantName}
        copyKey="seller"
        copiedKey={copiedKey}
        onCopy={copyField}
        copiedLabel={copiedLabel}
        copyLabel={copyLabel}
      />
      <CopyRow
        label={lang === "en" ? "Tax ID / EDRPOU" : "ЄДРПОУ / ІПН"}
        value={STORE_INFO.edrpou}
        copyKey="edrpou"
        copiedKey={copiedKey}
        onCopy={copyField}
        copiedLabel={copiedLabel}
        copyLabel={copyLabel}
      />
      <CopyRow
        label={lang === "en" ? "Bank" : "Банк"}
        value={STORE_INFO.bank}
        copyKey="bank"
        copiedKey={copiedKey}
        onCopy={copyField}
        copiedLabel={copiedLabel}
        copyLabel={copyLabel}
      />
      <CopyRow
        label="IBAN"
        value={STORE_INFO.ibanDisplay}
        copyKey="iban"
        copyValue={STORE_INFO.iban}
        copiedKey={copiedKey}
        onCopy={copyField}
        copiedLabel={copiedLabel}
        copyLabel={copyLabel}
      />
      <button
        type="button"
        className="sv2-copy-btn"
        style={{ margin: 10 }}
        onClick={() => void copyField("all-pay", formatSellerPaymentRequisitesText(lang))}
      >
        {copiedKey === "all-pay"
          ? copiedLabel
          : lang === "en"
            ? "Copy all requisites"
            : "Скопіювати всі реквізити"}
      </button>
      <ContactActionFeedback feedback={feedback} />
      {manualCopy ? (
        <div className="sv2-contact-feedback sv2-contact-feedback-info" style={{ margin: 10 }}>
          {t(lang, "copy_manually")}
          <textarea
            className="sv2-manual-copy"
            readOnly
            value={manualCopy}
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
      ) : null}
    </div>
  );
}

export function ViewSellerDetailsButton({ lang }: { lang: StoreV2Lang }) {
  return (
    <button
      type="button"
      className="sv2-about-link"
      onClick={() => {
        try {
          document.getElementById("sv2-seller-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
        } catch {
          /* ignore */
        }
      }}
    >
      {lang === "en" ? "View seller details" : "Переглянути дані продавця"}
    </button>
  );
}
